// externals
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner as LoadingIndicator } from '@interstate/components/Spinner';

// store
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as vehicleProtectionActionCreators from '@makemydeal/dr-dash-store/actions/vehicleProtectionActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// styles
import { LoaderContainer } from './EditDealAttributesDrawer.style';

// components
import DealAttributesForm from './dealAttributesForm/DealAttributesForm';
import { SlideOutWithFixedFooter } from '@makemydeal/dr-dash-components';
import { DiscardChangesModal } from '@makemydeal/dr-shared-components';

// utils
import { getRequestedDealAttributeInputs } from '../../utils';

// types
import type { VppDealAttributeInput } from '../../types';
import type { SurchargeOverrides } from '@makemydeal/dr-shared-types';

type EditDealAttributesDrawerProps = {
    show: boolean;
    onClose: () => void;
};

const EditDealAttributesDrawer = ({ show, onClose }: EditDealAttributesDrawerProps) => {
    const [updateDisabled, setUpdateDisabled] = useState(true);
    const [dealAttributeErrors, setDealAttributeErrors] = useState<{ [name: string]: boolean } | undefined>(undefined);
    const [showDiscardWarning, setShowDiscardWarning] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const isFetchingVppProducts = useSelector(vehicleProtectionSelectors.isFetchingVppProducts);
    const surchargesOverrides = useSelector(vehicleProtectionSelectors.getVppSurchargesOverrides);
    const { usedSurchargesAttributes, requiredSurchargesAttributes } = useSelector(
        vehicleProtectionSelectors.getVppRequestedSurcharges
    );
    const isUsageAndCustomerTypeSurchargesEnabled = useSelector(featureToggleSelectors.isUsageAndCustomerTypeSurchargesEnabled);
    const dispatch = useDispatch();
    const [editedDealAttributes, setEditedDealAttributes] = useState<SurchargeOverrides>({});

    // allows Interstate's SlidingDrawer to notice changes in content height and re-render the footer with overflow
    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [isFetchingVppProducts]);

    useEffect(() => {
        if (dealAttributeErrors) {
            setUpdateDisabled(Object.values(dealAttributeErrors).includes(true));
        }
    }, [dealAttributeErrors]);

    const handleClose = useCallback(() => {
        setUpdateDisabled(true);
        setShowDiscardWarning(false);
        setHasChanges(false);
        onClose();
    }, [onClose]);

    const onCancel = () => {
        if (hasChanges) {
            setShowDiscardWarning(true);
            return;
        }
        setEditedDealAttributes({});
        handleClose();
    };

    const handleGoBack = () => {
        setShowDiscardWarning(false);
    };

    const handleDiscardChanges = () => {
        setEditedDealAttributes({});
        handleClose();
    };

    const updateAndClose = () => {
        dispatch(
            vehicleProtectionActionCreators.updateVppSurchargesAttributes({
                ...surchargesOverrides,
                ...editedDealAttributes
            })
        );
        handleClose();
    };

    const onChangeDealAttribute = (dealAttribute: VppDealAttributeInput, withError?: boolean) => {
        setDealAttributeErrors({ ...dealAttributeErrors, [dealAttribute.name]: !!withError });
        setEditedDealAttributes((prevEditedDealAttributes: SurchargeOverrides) => ({
            ...prevEditedDealAttributes,
            [dealAttribute.name]: dealAttribute.value
        }));
        setHasChanges(true);
    };

    const onReRate = () => {
        updateAndClose();
    };

    let drawerContent: JSX.Element = (
        <LoaderContainer>
            <LoadingIndicator />
        </LoaderContainer>
    );

    if (!isFetchingVppProducts) {
        const visibleVppDealAttributes = getRequestedDealAttributeInputs(
            {
                ...surchargesOverrides,
                ...editedDealAttributes
            },
            usedSurchargesAttributes,
            requiredSurchargesAttributes,
            isUsageAndCustomerTypeSurchargesEnabled
        );

        drawerContent = <DealAttributesForm dealAttributeInputs={visibleVppDealAttributes} onChange={onChangeDealAttribute} />;
    }

    return (
        <>
            <SlideOutWithFixedFooter
                data-testid={'edit-deal-attributes'}
                show={show}
                onHide={onCancel}
                position="right"
                header="Edit Deal Attributes"
                panelWidth="min(846px, 95vw)"
                footer={{
                    layout: 'rightAligned',
                    options: [
                        {
                            action: onCancel,
                            label: 'Cancel',
                            customIdentifiers: {
                                optionButton0: {
                                    dataTestId: 'edit-deal-attributes-cancel-btn'
                                }
                            }
                        }
                    ],
                    primary: {
                        action: onReRate,
                        label: 'Update Attributes',
                        disabled: updateDisabled,
                        customIdentifiers: {
                            primaryButton: {
                                dataTestId: 'edit-deal-attributes-update-btn'
                            }
                        }
                    }
                }}
            >
                {drawerContent}
            </SlideOutWithFixedFooter>
            <DiscardChangesModal
                show={showDiscardWarning}
                onDiscard={handleDiscardChanges}
                onGoBack={handleGoBack}
                hasValidationErrors={updateDisabled}
            />
        </>
    );
};

export default EditDealAttributesDrawer;
